import type { CurrentAuthState, AuthAction } from "./state";
import {
  UNAUTHENTICATED,
  REDIRECTING,
  LOADING_ORGS,
  AWAITING_PASSWORD,
  AUTHENTICATING,
  AWAITING_ORG_SELECT,
  EXCHANGING,
  CREATING_SESSION,
  HYDRATING,
  AUTHENTICATED,
  AUTH_ERROR,
} from "./state";

export function authReducer(
  state: CurrentAuthState,
  action: AuthAction,
): CurrentAuthState {
  switch (action.type) {
    case "START_REDIRECT": {
      return {
        status: REDIRECTING,
        token: action.token,
        tokenType: action.tokenType,
      };
    }

    case "START_LOGIN": {
      return {
        status: LOADING_ORGS,
        email: action.email,
      };
    }

    case "ORGS_LOADED": {
      const { email, orgs, session } = action;

      // Auto-select if single org
      if (orgs.length === 1) {
        const org = orgs[0];

        // Password flow (no session) - go to password entry
        if (!session) {
          return {
            status: AWAITING_PASSWORD,
            email,
            orgId: org.org_id,
            orgs,
          };
        }

        // Magic link flow - auto-exchange
        return {
          status: EXCHANGING,
          email,
          orgId: org.org_id,
          session,
        };
      }

      // Multiple orgs - need user selection
      if (session) {
        return { status: AWAITING_ORG_SELECT, email, orgs, session };
      }

      // Password flow with multiple orgs - still need org select before password
      // Use empty session as placeholder; the password flow doesn't use session
      return { status: AWAITING_ORG_SELECT, email, orgs, session: "" };
    }

    case "SELECT_ORG": {
      if (state.status === AWAITING_ORG_SELECT) {
        // Magic link flow - has a real session, go to exchange
        if (state.session) {
          return {
            status: EXCHANGING,
            email: state.email,
            orgId: action.orgId,
            session: state.session,
          };
        }

        // Password flow - go to password entry
        return {
          status: AWAITING_PASSWORD,
          email: state.email,
          orgId: action.orgId,
          orgs: state.orgs,
        };
      }
      return state;
    }

    case "SUBMIT_PASSWORD": {
      if (state.status === AWAITING_PASSWORD) {
        return {
          status: AUTHENTICATING,
          email: state.email,
          orgId: state.orgId,
          password: action.password,
        };
      }
      return state;
    }

    case "AUTH_SUCCESS": {
      const { userInfo } = action;

      // Extract orgId from the current state
      let orgId: string | null = null;
      if (state.status === EXCHANGING || state.status === AUTHENTICATING) {
        orgId = state.orgId;
      }

      if (!orgId || !userInfo.token) {
        return {
          status: AUTH_ERROR,
          message: "Missing orgId or token after auth",
        };
      }

      const orgs = userInfo.orgs ?? [];

      // Signing in does not choose a warehouse, and does not need one. The
      // session is minted through /authz/exchange-token, which omits the
      // warehouse claims when none is given. Each product decides whether it
      // needs a warehouse and asks on the way in.
      //
      // An organisation with no warehouses signs in fine - it just cannot open
      // the products that are warehouse-scoped.
      return {
        status: CREATING_SESSION,
        email: userInfo.email ?? "",
        orgId,
        orgs,
        token: userInfo.token,
        userInfo,
        locationId: null,
      };
    }

    case "SELECT_LOCATION": {
      if (state.status === AUTHENTICATED) {
        return {
          ...state,
          status: CREATING_SESSION,
          locationId: action.locationId,
        };
      }
      return state;
    }

    case "SESSION_CREATED": {
      if (state.status === CREATING_SESSION) {
        return {
          ...state,
          status: AUTHENTICATED,
          sessionData: action.sessionData,
        };
      }
      return state;
    }

    case "REFRESH_SUCCESS": {
      if (state.status === HYDRATING) {
        const { userInfo, locationId, sessionData } = action;
        return {
          status: AUTHENTICATED,
          email: userInfo.email ?? "",
          orgId: state.orgId,
          orgs: userInfo.orgs ?? [],
          token: userInfo.token ?? state.token,
          userInfo,
          locationId,
          sessionData,
        };
      }
      return state;
    }

    case "ERROR": {
      return { status: AUTH_ERROR, message: action.message };
    }

    case "LOGOUT": {
      return { status: UNAUTHENTICATED };
    }

    case "HYDRATE": {
      return action.state;
    }
  }
}
